// The questions Cobrain has put to its owner (R25, D138), asked once per pattern (B47,
// migration 29) and brought to the user rather than left behind a tab (B57). A flag is one
// question about one message, but the question has a shape: the candidate tiers and the
// counterparty's domain. Since an answer applies to its whole pattern, asking per flag
// would ask the same thing thousands of times — so the API groups by pattern, and this
// prompt asks the shape, once, with the number it settles.
//
// A tier question carries a third answer, "Not relevant" (B63): a newspaper's mailing list
// belongs to no tier, and a question with no true answer stops a queue rather than being
// answered wrongly. It is set apart from the tiers because it is a different kind of thing
// to say — not "which of these" but "none of these, and stop asking".
//
// A tier question also says what it does not decide (B61). Two equal buttons and no
// consequence made an item that is honestly two things — a contract negotiation is Legal
// and Commercial both — read as a question with no true answer. Both sentences state rules
// that already hold: a participant sees an item whatever its tier (D106, app.signal_visible
// in schema file 10), and assign_tier/v1.md tells the brain to take the narrower tier when
// an item's content reaches two, which is why its guess is the one offered first.
//
// The prompt is a native <dialog> opened with showModal(): the top layer, the focus trap,
// the Escape key and the inert background are the platform's, not a hand-rolled trap that
// would get one of them wrong. Largest pattern first, because that is the answer that
// clears the most. Skipping moves the queue on and closing stops the asking; neither
// writes anything, because an answer is a person's judgement (it overrules the brain's and
// trains the classifier) and nothing is inferred from silence.

import { Chip } from './parts/primitives.js';
import { ActionSelect, ActionButton } from './parts/ActionButton.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, Unauthenticated, type Answer, type FlagPattern, type PersonalArea, type Store } from './api.js';
import { ALL_TIERS, count, formatDate, patternSubtitle, settlesLine, tierLabel, tierName } from './labels.js';
import { positionLine, reduce, remaining, sittingLine, start, type QueueState } from './queue.js';

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; flags: FlagPattern[]; areas: PersonalArea[] }
  | { kind: 'error'; message: string };

export interface Questions {
  load: Load;
  /**
   * The queue as it is walked: the loaded patterns, less the ones held back by B95. The
   * prompt reads this and never load.flags, which is everything that came back -- deriving
   * the queue twice is how the filter would quietly stop applying.
   */
  flags: FlagPattern[];
  q: QueueState;
  busy: boolean;
  /** The open flags still behind the queue: what loaded, less what this sitting settled. */
  behind: number;
  /** The patterns not yet answered or skipped: what the bar counts. */
  left: number;
  /**
   * B95: the questions held back because the tier choice moves nobody in this store today.
   * They are not withdrawn and not lost: they come back on their own when a second person
   * exists whose sight the tier decides, and they can be opened now with `showWaiting`.
   */
  waiting: number;
  showWaiting: boolean;
  setShowWaiting(v: boolean): void;
  answer(a: Answer): Promise<void>;
  skip(): void;
}

/**
 * The owner's open questions and the state of answering them. Lives above the prompt so
 * the bar can carry the count and the prompt can be opened and closed without losing the
 * user's place in the queue.
 *
 * REQ-2026-10-04-15: the pill is on every page and needs a number; the card is on triage alone and needs the
 * questions. So every page reads the count (GET /api/privacy-flags/count: the same patterns and the same B95 test as
 * the list), and the questions are read from the first time `wantList` is true (triage, where the pill also goes), then
 * kept for the session as before. Every page used to read all of them, 683 patterns and about eight seconds on the
 * live store, to show one number.
 */
export function useQuestions(store: Store, wantList: boolean): Questions {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [counted, setCounted] = useState<{ asking: number; waiting: number } | null>(null);
  const [listed, setListed] = useState(wantList);
  const [q, setQ] = useState<QueueState>(start);
  const [busy, setBusy] = useState(false);
  const [showWaiting, setShowWaiting] = useState(false);

  useEffect(() => { if (wantList) setListed(true); }, [wantList]);

  useEffect(() => {
    let cancelled = false;
    api.privacyFlagCount(store.tenantId)
      .then((r) => { if (!cancelled) setCounted({ asking: r.asking, waiting: r.waiting }); })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Unauthenticated) window.location.href = '/auth/login';
        // Otherwise the pill waits for the list, as it always did.
      });
    return () => { cancelled = true; };
  }, [store.tenantId]);

  useEffect(() => {
    if (!listed) return;
    let cancelled = false;
    api.privacyFlags(store.tenantId)
      .then((r) => { if (!cancelled) { setLoad({ kind: 'ready', flags: r.flags, areas: r.areas }); setQ(start); } })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        setLoad({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
      });
    return () => { cancelled = true; };
  }, [listed, store.tenantId]);

  // B95: the queue a person is walked through is the questions that decide something for
  // somebody today. The rest are behind one line of text, in the same dialog, answerable
  // the moment he wants them -- the tier card is also the way to say "not relevant" and
  // "this is mine", and those act whatever the tier.
  const all = load.kind === 'ready' ? load.flags : [];
  const flags = showWaiting ? all : all.filter((f) => !f.waitsForASecondUser);
  // Until the questions are read the pill counts from the count; once read, from them, as before. A list that failed
  // shows no pill and no card, as before.
  const fromCount = load.kind === 'loading' && counted !== null;
  const waiting = fromCount ? counted.waiting : all.filter((f) => f.waitsForASecondUser).length;
  const openAtLoad = flags.reduce((n, f) => n + f.patternCount, 0);
  const left = fromCount ? (showWaiting ? counted.asking + counted.waiting : counted.asking) : Math.max(flags.length - q.at, 0);

  const answer = useCallback(async (a: Answer) => {
    const flag = flags[q.at];
    if (!flag) return;
    setBusy(true);
    try {
      const { flag: done } = await api.answerFlag(store.tenantId, flag.id, a);
      setQ((s) => reduce(s, { kind: 'answered', settledByPattern: done.settledByPattern }));
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      // 409: another sitting, or a pattern, settled it meanwhile. Not a failure.
      if (e instanceof ApiError && e.status === 409) setQ((s) => reduce(s, { kind: 'gone' }));
      else setQ((s) => reduce(s, { kind: 'failed', message: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  }, [flags, q.at, store.tenantId]);

  const skip = useCallback(() => setQ((s) => reduce(s, { kind: 'skip' })), []);

  return { load, flags, q, busy, behind: remaining(openAtLoad, q.settled), left,
           waiting, showWaiting, setShowWaiting: (v: boolean) => { setShowWaiting(v); setQ(start); }, answer, skip };
}

const KINDS: Record<string, string> = {
  email: 'Email', teams_chat: 'Teams chat', teams_channel: 'Teams channel', cobrain_chat: 'Cobrain',
  whatsapp: 'WhatsApp', call: 'Call', meeting: 'Meeting', calendar_event: 'Calendar event',
  document: 'Document', note: 'Note', news_item: 'News', digest: 'Digest', manual_note: 'Note',
};

/**
 * The item, as much of it as it takes to judge (B83). Mike, on a subjectless item: "there is
 * no subject so I have no way of allocating this." A domain and an empty subject line is not
 * an item. So: who it is actually from, what kind of thing it is, and how it opens.
 */
function Example({ flag }: { flag: FlagPattern }) {
  const who = flag.fromName ?? flag.fromAddress;
  if (flag.subject === null && flag.occurredAt === null && who === null && flag.preview === null) return null;
  return (
    <div className="example">
      <p className="example-head">
        {flag.subject ?? <span className="dim">(no subject)</span>}
        {flag.occurredAt ? <span className="meta"> · {formatDate(flag.occurredAt)}</span> : null}
      </p>
      {who || flag.kind ? (
        <p className="example-from">
          {flag.kind ? (KINDS[flag.kind] ?? flag.kind) : null}
          {flag.kind && who ? ' · ' : null}
          {who ? <>from {who}{flag.fromName && flag.fromAddress ? <span className="meta"> ({flag.fromAddress})</span> : null}</> : null}
        </p>
      ) : null}
      {flag.preview ? <p className="example-preview">{flag.preview}</p> : null}
    </div>
  );
}

/**
 * The tier question (B81). What Mike asked for, restored: **you select, then you submit.**
 *
 * He asked for it plainly — "allow me to select both of your options, then hit submit" —
 * and B67 built it. Then, misreading a different complaint, B75 tore it out and made a
 * click answer instead, which removed the very thing he had asked for; B80 put a "+ also"
 * on the row, which was another way of not doing what was asked. Three rebuilds, two of
 * them undoing a request that had been clear the first time.
 *
 * So: clicking a tier selects it. The first is the tier the privacy rails read, the second
 * is the filing, both are marked on the row, and one Answer button ends it. The other
 * answers are their own act and stay one click, because they are not a choice among tiers:
 * an area moves the item to the personal store, "not relevant" keeps it and stops the
 * asking.
 *
 * The rule: when someone tells you the shape of the control they want, build that shape.
 * Do not improve it into something else.
 */
/**
 * What the chooser offers, so the item page's Change (pipeline R3, module 10 U315 to U318) draws the card's own
 * choices. The card offers the brain's candidates, its guess first; the item page offers the tier the item is filed
 * under now, tagged "Filed now", and its button reads "Re-file" (U318). "Not relevant" says what it does where it is
 * pressed: on the card it teaches the sender; on the item page it is about that one item.
 */
export interface TierOffer {
  offered: string[];
  leadTag: string;
  recommendedTier: string | null;
  recommendedFrom: { companyType: string; answers: number } | null;
  submitLabel: string;
  notRelevantGist: string;
}

const cardOffer = (flag: FlagPattern): TierOffer => ({
  offered: flag.candidateTiers ?? [], leadTag: 'Cobrain’s guess', recommendedTier: flag.recommendedTier, recommendedFrom: flag.recommendedFrom,
  submitLabel: 'Answer', notRelevantGist: 'Kept and searchable, never in triage, and Cobrain stops asking about this sender.',
});

export function TierChoices({ offer, areas, busy, onAnswer }: { offer: TierOffer; areas: PersonalArea[]; busy: boolean; onAnswer: (a: Answer) => void }) {
  const { offered } = offer;
  const [chosen, setChosen] = useState<string[]>([]);
  const [tier, also] = chosen;

  const toggle = (t: string) =>
    setChosen((c) => (c.includes(t) ? c.filter((x) => x !== t) : c.length >= 2 ? [c[1]!, t] : [...c, t]));

  const row = (t: string) => {
    const label = tierLabel(t);
    const at = chosen.indexOf(t);
    return (
      <ActionButton key={t} className={at < 0 ? 'choice' : 'choice on'} aria-pressed={at >= 0} disabled={busy} onClick={() => toggle(t)}>
        <strong>{tierName(t)}</strong>
        {t === offered[0] ? <>{' '}<span className="tag">{offer.leadTag}</span></> : null}
        {t === offer.recommendedTier ? <>{' '}<span className="tag">What you usually say</span></> : null}
        {at >= 0 ? <span className="pick">{at === 0 ? 'filed here' : 'also'}</span> : null}
        {label ? <span className="gist">{label.gist}</span> : null}
      </ActionButton>
    );
  };

  // B94: the correspondent's HubSpot company type, and what this person has chosen for
  // others of that type. It is shown and not applied: a default that answered itself
  // would train the classifier on Cobrain's guess rather than on Mike's judgement.
  const rec = offer.recommendedFrom && offer.recommendedTier
    ? `You have filed ${offer.recommendedFrom.answers === 1 ? 'one other' : `${offer.recommendedFrom.answers} other`} ${offer.recommendedFrom.companyType.toLowerCase()}${offer.recommendedFrom.answers === 1 ? '' : 's'} under ${tierName(offer.recommendedTier)}.`
    : null;

  return (
    <>
      {rec ? <p className="note">{rec} Cobrain has not applied it: the answer is yours.</p> : null}
      <div className="choices">
        {offered.map(row)}
        {chosen.filter((t) => !offered.includes(t)).map(row)}
        {offer.recommendedTier && !offered.includes(offer.recommendedTier) && !chosen.includes(offer.recommendedTier) ? row(offer.recommendedTier) : null}
      </div>

      <label className="elsewhere">
        <span>Somewhere else</span>
        <ActionSelect value="" disabled={busy} onChange={(e) => { if (e.target.value) toggle(e.target.value); }}>
          <option value="">Add another tier to the selection…</option>
          {ALL_TIERS.filter((t) => !offered.includes(t) && !chosen.includes(t)).map((t) => (
            <option key={t} value={t}>{tierName(t)} — {tierLabel(t)?.gist}</option>
          ))}
        </ActionSelect>
      </label>

      <div className="submit-row">
        <ActionButton className="button" disabled={busy || !tier}
                onClick={() => onAnswer(also ? { tier: tier!, alsoTier: also } : { tier: tier! })}>
          {offer.submitLabel}
        </ActionButton>
        <span className="submit-said">
          {!tier ? 'Choose one, or two when it is honestly both.'
                 : also ? <>Filed under <strong>{tierName(tier!)}</strong>, also <strong>{tierName(also)}</strong>. </>
                        : <>Filed under <strong>{tierName(tier!)}</strong>. </>}
          {also ? <ActionButton className="link inline" disabled={busy} onClick={() => setChosen([also, tier!])}>Swap</ActionButton> : null}
        </span>
      </div>

      {areas.length > 0 ? (
        <div className="mine">
          <p className="mine-head">Or it is mine, not the company’s — file it under:</p>
          <div className="mine-areas">
            {areas.map((a) => (
              <ActionButton key={a.id} className="chip" disabled={busy} title={a.gist ?? undefined}
                      onClick={() => onAnswer({ personalArea: a.id })}>{a.name}</ActionButton>
            ))}
          </div>
          <p className="gist">It moves to your private store, where you alone can read it.</p>
        </div>
      ) : null}

      <ActionButton className="choice choice-aside" disabled={busy} onClick={() => onAnswer({ notRelevant: true })}>
        <strong>Not relevant to me</strong>
        <span className="gist">{offer.notRelevantGist}</span>
      </ActionButton>
    </>
  );
}

function TierQuestion({ flag, areas, busy, onAnswer }: { flag: FlagPattern; areas: PersonalArea[]; busy: boolean; onAnswer: (a: Answer) => void }) {
  return (
    <>
      <h2 id="ask-title">Which is this?</h2>
      <p className="plain">Cobrain is torn on {patternSubtitle(flag.patternKey)}.</p>
      <Example flag={flag} />
      <TierChoices offer={cardOffer(flag)} areas={areas} busy={busy} onAnswer={onAnswer} />
      <p className="note">
        Everyone on this message sees it either way — the tier only decides who else can find it.
      </p>
    </>
  );
}

function PersonalQuestion({ flag, busy, onAnswer }: { flag: FlagPattern; busy: boolean; onAnswer: (a: Answer) => void }) {
  return (
    <>
      <h2 id="ask-title">Personal or business?</h2>
      <p className="plain">Cobrain is unsure about this passage in {patternSubtitle(flag.patternKey)}.</p>
      {flag.passage ? <blockquote className="passage">{flag.passage}</blockquote> : null}
      <Example flag={flag} />
      <div className="choices">
        <ActionButton className="choice" disabled={busy} onClick={() => onAnswer({ personal: true })}>
          <strong>Personal</strong>
          <span className="gist">It moves to your private store, where you alone can read it.</span>
        </ActionButton>
        <ActionButton className="choice" disabled={busy} onClick={() => onAnswer({ personal: false })}>
          <strong>Business</strong>
          <span className="gist">It stays in the commercial store, under its tier.</span>
        </ActionButton>
      </div>
    </>
  );
}

/**
 * The questions, as a card at the top of the ten (U18, module 04 Open 29, artboard
 * RecentAndQuestions.dc.html; B181). The content is the prompt's, unchanged: the largest
 * pattern first, the tier chooser Mike asked for (B81: select, then submit), "it is mine",
 * "not relevant", skip, not now, and the held-back questions behind one line (B95). What
 * changed is the frame: a modal dialog over the page (B57) became one item in the shape of
 * the ten, drawn while any tier question remains, opened and folded from the "N questions"
 * pill in the bar. Folded is not answered: nothing is inferred from silence.
 */
export function QuestionsCard({ questions, expanded, onToggle }: { questions: Questions; expanded: boolean; onToggle: () => void }) {
  const { load, flags, q, busy, behind, waiting, showWaiting, setShowWaiting, answer, skip } = questions;
  const flag = flags[q.at];
  const drawn = !(load.kind === 'loading' || (load.kind === 'ready' && flags.length === 0 && waiting === 0));
  // The questions are read when triage is (REQ-2026-10-04-15), so the pill pressed on another page opens a card that
  // is not drawn yet, and the bar's scroll to it finds nothing: the card brings itself into view when it first draws
  // opened. Drawn folded first, it leaves the scrolling to the bar, as before.
  const article = useRef<HTMLElement | null>(null);
  const seen = useRef(false);
  useEffect(() => {
    if (!drawn) return;
    if (!seen.current && expanded) article.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    seen.current = true;
  }, [drawn, expanded]);
  if (!drawn) return null;

  const waitingLine = waiting > 0 && !showWaiting ? (
    <p className="note">
      {count(waiting, 'question')} about who else may see these are waiting: as the rails stand, nobody in this
      store sees a difference either way. Cobrain will ask them when somebody does.{' '}
      <ActionButton reading className="link" onClick={() => setShowWaiting(true)}>Show them anyway</ActionButton>
    </p>
  ) : showWaiting ? (
    <p className="note">
      Showing every question, including {waiting === 1 ? 'the one' : `the ${waiting}`} whose tier moves nobody in this store today.{' '}
      <ActionButton reading className="link" onClick={() => setShowWaiting(false)}>Hide them again</ActionButton>
    </p>
  ) : null;

  return (
    <article ref={article} className="item questions" data-item="questions" aria-labelledby="ask-title">
      <div className="meta">
        <Chip className="brain">Cobrain is torn</Chip>
        {flag ? <><span>{positionLine(q.at, flags.length).toLowerCase()}</span><span>{count(behind, 'message')} waiting on them</span></> : null}
        {flag ? <span className="when"><ActionButton reading className="link inline" disabled={busy} onClick={skip}>skip</ActionButton> <ActionButton reading className="link inline" onClick={onToggle}>{expanded ? 'not now' : 'answer'}</ActionButton></span>
              : <span className="when"><ActionButton reading className="link inline" onClick={onToggle}>{expanded ? 'fold' : 'open'}</ActionButton></span>}
      </div>
      {!expanded ? (
        <p className="head" id="ask-title">{flag ? (flag.questionKind === 'tier' ? `Which is this: ${patternSubtitle(flag.patternKey)}?` : `Personal or business: ${patternSubtitle(flag.patternKey)}?`) : 'Your questions'}</p>
      ) : (
        <>
          {load.kind === 'error' ? <p className="plain" id="ask-title">Cobrain could not read your questions just now. {load.message}</p> : null}
          {load.kind === 'ready' ? waitingLine : null}
          {load.kind === 'ready' && flags.length > 0 && !flag ? (
            <>
              <p className="head" id="ask-title">That is the last of them.</p>
              <p className="note">{sittingLine(q)}</p>
            </>
          ) : null}
          {flag ? (
            <div aria-live="polite">
              {/* Keyed by the flag: the next question is a fresh card, not the last one wearing new text (B68). */}
              {flag.questionKind === 'tier'
                ? <TierQuestion key={flag.id} flag={flag} areas={load.kind === 'ready' ? load.areas : []} busy={busy} onAnswer={(a) => void answer(a)} />
                : <PersonalQuestion key={flag.id} flag={flag} busy={busy} onAnswer={(a) => void answer(a)} />}
              <p className="settles">{settlesLine(flag.patternCount)}</p>
              {q.said ? <p className="note said">{q.said}</p> : null}
            </div>
          ) : null}
        </>
      )}
    </article>
  );
}

